import {
  DUPLICATE_RESOLUTIONS,
  DUPLICATE_RESOLUTION_LABELS,
  IMPORT_ROW_STATUS_LABELS,
  type DuplicateResolution,
  type ImportJobDTO,
  type ImportRowDTO,
  type ImportRowQualityBody,
  type ImportRowStatus,
  type LeadQualityVerdict,
  leadQualityVerdict,
} from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { Badge, Button, Card, Checkbox, Pagination, Tabs, Tooltip, useToast } from '../../components/ui';
import { api, errorMessage } from '../../lib/api';

type Filter = 'duplicate' | 'invalid' | 'junk' | 'imported' | 'merged' | 'skipped' | 'failed';

const PAGE_SIZE = 200;

const ROW_COLORS: Record<ImportRowStatus, string> = {
  pending: 'slate',
  valid: 'green',
  invalid: 'red',
  junk: 'orange',
  duplicate: 'amber',
  imported: 'green',
  merged: 'violet',
  skipped: 'gray',
  failed: 'red',
};

/** How the scan rated a row that is (or may still be) going in; a junk score on a kept row means someone included it. */
const QUALITY_BADGE: Record<LeadQualityVerdict, { label: string; color: string }> = {
  good: { label: 'Good lead', color: 'emerald' },
  weak: { label: 'Weak lead', color: 'amber' },
  junk: { label: 'Included anyway', color: 'gray' },
};

/**
 * Rows of an import, with what still needs a person: possible duplicates to decide and junk to import anyway. Each
 * decision is applied right away (the job goes back to 'importing' and the page polls until it settles).
 */
export function ImportReview({ job, filters }: { job: ImportJobDTO; filters: { key: Filter; count: number }[] }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const firstWithRows = filters.find((f) => f.count > 0)?.key ?? filters[0]!.key;
  const [filter, setFilter] = useState<Filter>(firstWithRows);
  const [page, setPage] = useState(1);
  // Row ids ticked on this tab; kept while paging so a selection can span pages.
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const editable = job.status === 'validated' || job.status === 'completed';
  // Invalid rows can never be imported, and rows already in need no decision.
  const selectable = editable && (filter === 'duplicate' || filter === 'junk');
  const { data } = useQuery({
    queryKey: ['imports', job.id, 'rows', filter, page],
    queryFn: ({ signal }) =>
      api.get<{ items: ImportRowDTO[]; total: number }>(`/api/imports/${job.id}/rows?status=${filter}&page=${page}&pageSize=${PAGE_SIZE}`, { signal }),
  });
  // Decided rows leave the tab; when that empties the last page, step back to the new last one.
  useEffect(() => {
    if (data && data.items.length === 0 && page > 1) setPage(Math.max(1, Math.ceil(data.total / PAGE_SIZE)));
  }, [data, page]);
  // Decisions are applied in the background ('importing'): when the import settles, rows have moved between tabs.
  const seenStatus = useRef(job.status);
  useEffect(() => {
    if (seenStatus.current === job.status) return;
    seenStatus.current = job.status;
    void queryClient.invalidateQueries({ queryKey: ['imports', job.id, 'rows'] });
  }, [job.status, job.id, queryClient]);
  const changeFilter = (next: Filter) => {
    setFilter(next);
    setPage(1);
    setSelected(new Set());
  };
  const clearSelection = () => setSelected(new Set());
  const resolve = useMutation({
    mutationFn: (body: { rowIds?: string[]; allUnresolved?: boolean; resolution: DuplicateResolution }) => api.post<ImportJobDTO>(`/api/imports/${job.id}/resolve`, body),
    onSuccess: async (next) => {
      queryClient.setQueryData(['imports', job.id], next);
      await queryClient.invalidateQueries({ queryKey: ['imports', job.id, 'rows'] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
  const quality = useMutation({
    mutationFn: (body: ImportRowQualityBody) => api.post<ImportJobDTO>(`/api/imports/${job.id}/rows/quality`, body),
    onSuccess: async (next) => {
      queryClient.setQueryData(['imports', job.id], next);
      await queryClient.invalidateQueries({ queryKey: ['imports', job.id, 'rows'] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const rows = data?.items ?? [];
  const pageIds = rows.map((row) => row.id);
  const pageTicked = pageIds.filter((id) => selected.has(id)).length;
  const rowIds = [...selected];
  const count = selected.size;
  const toggle = (ids: readonly string[], checked: boolean) =>
    setSelected((current) => {
      const next = new Set(current);
      for (const id of ids) {
        if (checked) next.add(id);
        else next.delete(id);
      }
      return next;
    });

  return (
    <Card>
      <Tabs tabs={filters.map((f) => ({ key: f.key, label: IMPORT_ROW_STATUS_LABELS[f.key], count: f.count }))} value={filter} onChange={changeFilter} />
      {editable && filter === 'duplicate' && job.unresolvedDuplicates > 0 && (
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 bg-amber-50 px-4 py-2 text-[13px] text-amber-900">
          {job.unresolvedDuplicates} undecided — for all of them:
          {DUPLICATE_RESOLUTIONS.map((resolution) => (
            <Button key={resolution} size="sm" loading={resolve.isPending} onClick={() => resolve.mutate({ allUnresolved: true, resolution })}>
              {DUPLICATE_RESOLUTION_LABELS[resolution]}
            </Button>
          ))}
        </div>
      )}
      {filter === 'junk' && job.junkRows > 0 && (
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 bg-orange-50 px-4 py-2 text-[13px] text-orange-900">
          {editable ? 'The scan set these rows aside because they don’t look like real leads; they were not imported. Import any of them anyway if it got it wrong.' : 'The scan set these rows aside; they were not imported.'}
          {editable && (
            <Button size="sm" className="ml-auto" loading={quality.isPending} onClick={() => quality.mutate({ allJunk: true, action: 'include' })}>
              Include all {job.junkRows}
            </Button>
          )}
        </div>
      )}
      {selectable && rows.length > 0 && (
        <div className={clsx('flex flex-wrap items-center gap-2 border-b px-4 py-2 text-[13px]', count > 0 ? 'border-ember-200 bg-ember-50' : 'border-slate-100')}>
          <Checkbox
            label="Select all on this page"
            checked={pageTicked === pageIds.length}
            indeterminate={pageTicked > 0 && pageTicked < pageIds.length}
            onChange={(event) => toggle(pageIds, event.target.checked)}
          />
          {/* Only with a selection: the duplicates tab must not gain another "Skip" before the per-row ones. */}
          {count > 0 && (
            <>
              <span className="font-medium text-ember-900">{count} selected</span>
              <div className="ml-auto flex flex-wrap items-center gap-1.5">
                {filter === 'duplicate' && (
                  <>
                    {DUPLICATE_RESOLUTIONS.map((resolution) => (
                      <Button key={resolution} size="sm" loading={resolve.isPending} onClick={() => resolve.mutate({ rowIds, resolution }, { onSuccess: clearSelection })}>
                        {DUPLICATE_RESOLUTION_LABELS[resolution]} ({count})
                      </Button>
                    ))}
                    <Button size="sm" loading={quality.isPending} onClick={() => quality.mutate({ rowIds, action: 'exclude' }, { onSuccess: clearSelection })}>
                      Not a lead
                    </Button>
                  </>
                )}
                {filter === 'junk' && (
                  <Button size="sm" loading={quality.isPending} onClick={() => quality.mutate({ rowIds, action: 'include' }, { onSuccess: clearSelection })}>
                    Import {count} anyway
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={clearSelection}>
                  Clear selection
                </Button>
              </div>
            </>
          )}
        </div>
      )}
      <ul className="divide-y divide-slate-100">
        {rows.map((row) => (
          <li key={row.id} className="flex flex-wrap items-start gap-3 px-4 py-2 text-[13px]">
            {selectable && (
              <Checkbox
                aria-label={`Select line ${row.rowNumber + 1}`}
                checked={selected.has(row.id)}
                onChange={(event) => toggle([row.id], event.target.checked)}
                className="mt-0.5"
              />
            )}
            <span className="w-14 shrink-0 pt-0.5 text-xs text-slate-400">Line {row.rowNumber + 1}</span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-slate-900">{row.preview.name ?? '(no name)'}</span>
                <span className="text-slate-500">{[row.preview.city, row.preview.phone, row.preview.contact].filter(Boolean).join(' · ')}</span>
                <Badge color={ROW_COLORS[row.status]} size="sm">
                  {IMPORT_ROW_STATUS_LABELS[row.status]}
                </Badge>
                {row.qualityScore !== null && row.status !== 'junk' && row.status !== 'invalid' && (
                  <Tooltip content={row.qualityReasons.length > 0 ? row.qualityReasons.join(' · ') : null}>
                    <Badge color={QUALITY_BADGE[leadQualityVerdict(row.qualityScore)].color} size="sm" dot>
                      {QUALITY_BADGE[leadQualityVerdict(row.qualityScore)].label}
                    </Badge>
                  </Tooltip>
                )}
                {row.businessId && (
                  <Link to={`/leads/${row.businessId}`} className="text-ember-700 hover:underline">
                    Open lead
                  </Link>
                )}
              </div>
              {row.duplicate && (
                <p className="text-amber-800">
                  Looks like{' '}
                  <Link to={`/leads/${row.duplicate.businessId}`} className="underline">
                    {row.duplicate.name}
                  </Link>
                  {row.duplicate.city ? ` (${row.duplicate.city})` : ''} — {row.duplicate.confidence}%: {row.duplicate.reasons.join(', ')}
                </p>
              )}
              {row.duplicateOfRow !== null && (
                <p className="text-amber-800">
                  Same place as line {row.duplicateOfRow + 1} of this file
                  {row.duplicateOfRowMatch ? ` — ${row.duplicateOfRowMatch.confidence}%: ${row.duplicateOfRowMatch.reasons.join(', ')}` : ''}
                </p>
              )}
              {row.status === 'junk' && row.qualityReasons.length > 0 && <p className="text-orange-800">{row.qualityReasons.join(' · ')}</p>}
              {row.errors.map((error) => (
                <p key={error} className="text-red-700">
                  {error}
                </p>
              ))}
              {row.warnings.map((warning) => (
                <p key={warning} className="text-xs text-slate-500">
                  {warning}
                </p>
              ))}
            </div>
            {row.status === 'duplicate' && (
              <div className="flex gap-1">
                {DUPLICATE_RESOLUTIONS.map((resolution) => (
                  <Button
                    key={resolution}
                    size="sm"
                    variant={row.resolution === resolution ? 'primary' : 'ghost'}
                    disabled={!editable || (resolution === 'merge' && !row.duplicate && row.duplicateOfRow === null)}
                    onClick={() => resolve.mutate({ rowIds: [row.id], resolution })}
                  >
                    {DUPLICATE_RESOLUTION_LABELS[resolution]}
                  </Button>
                ))}
              </div>
            )}
            {editable && row.status === 'junk' && (
              <Button size="sm" disabled={quality.isPending} onClick={() => quality.mutate({ rowIds: [row.id], action: 'include' })}>
                Import anyway
              </Button>
            )}
            {editable && row.status === 'duplicate' && (
              <Button size="sm" variant="ghost" disabled={quality.isPending} onClick={() => quality.mutate({ rowIds: [row.id], action: 'exclude' })}>
                Not a lead
              </Button>
            )}
          </li>
        ))}
        {data && data.items.length === 0 && <li className="px-4 py-6 text-center text-[13px] text-slate-500">No rows here.</li>}
      </ul>
      {data && data.total > PAGE_SIZE && (
        <div className="border-t border-slate-200 px-4 py-2">
          <Pagination page={page} pageSize={PAGE_SIZE} total={data.total} onChange={setPage} />
        </div>
      )}
    </Card>
  );
}
